import React from 'react';
import _merge from 'lodash/merge';
import { getContext } from 'common';
import Elevation, { ElevationProps } from 'components/Elevation';
import ResizeHandleIcon from '@nokia-csf-uxr/ccfk-assets/ResizeHandleIcon';
import { Rnd } from 'react-rnd';
import { zIndex } from 'styles';
import { HEADER_CLASSNAME } from './FloatingPanelHeader';
import Styled, { MIN_HEIGHT, MIN_WIDTH } from './styles';

const RND_DEFAULT_STYLES = {
  outline: 'none',
  zIndex: zIndex.FLOATING_PANEL
};

const getResizeHandlerStyles = RTL => ({
  position: 'absolute' as const,
  bottom: 0,
  ...(RTL
    ? {
      left: -2,
      right: 'auto',
      cursor: 'nesw-resize',
      direction: 'rtl' as const
    }
    : { right: -2, cursor: 'nwse-resize' }),
  userSelect: 'none' as const,
  zIndex: zIndex.FLOATING_PANEL
});

export interface FloatingPanelProps extends React.ComponentPropsWithRef<'div'> {
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** Content of FloatingPanel */
  children?: React.ReactNode;
  /** Forward props to the `Elevation` component. */
  elevationProps?: Partial<ElevationProps>;
  /** Displays the floating panel in fullscreen. */
  fullscreen?: boolean;
  /** Forward props to 'react-rnd' package. See http://github.com/bokuweb/react-rnd for documentation. */
  rndProps?: {
    style?: React.CSSProperties,
    onDragStart?: (any) => void;
    onDragStop?: (any) => void;
  };
}

/** Floating Panel is an interactive window where the content inside the window can be adjusted and configured depending on the application. It is also draggable and resizable and can be minimized, maximized or closed. */
const FloatingPanel = React.forwardRef<HTMLElement, FloatingPanelProps>((props: FloatingPanelProps, ref) =>
  getContext(({ RTL, theme }) => {
    const {
      children, theme: propTheme, elevationProps, rndProps, fullscreen, ...otherProps
    } = props;
    const resolvedTheme = _merge({}, theme.FLOATING_PANEL, propTheme);
    const rndRef = React.useRef(null);
    const [isDragging, setIsDragging] = React.useState(false);
    const {
      style: rndStyle, onDragStart, onDragStop, ...otherRndProps
    } = rndProps;

    return (
      <Rnd
        ref={rndRef}
        role="alertdialog"
        aria-modal="true"
        tabIndex="-1"
        // @ts-expect-error: Moe to review
        default={{
          x: 0,
          y: 0
        }}
        style={{ ...RND_DEFAULT_STYLES, ...rndStyle }}
        minHeight={MIN_HEIGHT}
        minWidth={MIN_WIDTH}
        dragHandleClassName={HEADER_CLASSNAME}
        resizeHandleStyles={{
          bottomRight: !RTL ? getResizeHandlerStyles(RTL) : undefined,
          bottomLeft: RTL ? getResizeHandlerStyles(RTL) : undefined
        }}
        resizeHandleComponent={{
          bottomRight: !RTL && <ResizeHandleIcon size="0.875rem" />,
          bottomLeft: RTL && <ResizeHandleIcon size="0.875rem" />
        }}
        enableResizing={{
          top: false,
          right: false,
          bottom: false,
          left: false,
          topRight: false,
          bottomRight: !RTL && !fullscreen,
          bottomLeft: RTL && !fullscreen,
          topLeft: false
        }}
        onDragStart={(...args) => {
          setIsDragging(true);
          onDragStart && onDragStart(args);
        }}
        onDragStop={(...args) => {
          setIsDragging(false);
          onDragStop && onDragStop(args);
        }}
        bounds="window"
        disableDragging={fullscreen}
        {...otherRndProps}
      >
        <Elevation
          elevationIndex={isDragging ? 24 : 16}
          {...elevationProps}
          style={{ width: '100%', height: '100%', ...(elevationProps && elevationProps.style) }}
        >
          <Styled.FloatingPanel $fullscreen={fullscreen} $theme={resolvedTheme} $rtl={RTL} ref={ref} {...otherProps}>
            {children}
          </Styled.FloatingPanel>
        </Elevation>
      </Rnd>
    );
  }));

FloatingPanel.displayName = 'FloatingPanel';

FloatingPanel.defaultProps = {
  theme: {},
  children: undefined,
  elevationProps: undefined,
  fullscreen: false,
  rndProps: {}
};

export default FloatingPanel;
